import { router, usePage } from "@inertiajs/react";
import * as React from "react";
import { toast } from "sonner";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import {
  navigateToUrl,
  statusKey,
} from "@/bizminer/lib/heatmap/heatmapUtils";
import { cn } from "@/lib/utils";
import {
  bizminer_app_reports_export_heatmap_pptx_path,
  bizminer_app_reports_path,
} from "@/routes";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";
import type {
  BizminerHeatmapSegment,
  BizminerHeatmapTaxonomy,
} from "@/types/serializers";
import {
  BENCHMARK_SUBTITLES,
  type BenchmarkMode,
} from "./benchmarkMode";
import HeatmapGroupCard from "./_components/HeatmapGroupCard";
import HeatmapDrawer from "./_components/HeatmapDrawer";
import HeatmapHeader from "./_components/HeatmapHeader";
import HeatmapTileButton from "./_components/HeatmapTileButton";
import {
  useHeatmapState,
  type HeatmapBenchmarksProp,
  type HeatmapScoresProp,
  type HeatmapKpiDriverScoresProp,
  type HeatmapKpiMappingProp,
  type HeatmapGroupScoresProp,
  type HeatmapPillarInsightIdsProp,
} from "./useHeatmapState";

export default function Heatmap() {
  const {
    props: {
      heatmap_accounts,
      heatmap_scores,
      heatmap_benchmarks,
      heatmap_kpi_mapping,
      heatmap_kpi_driver_scores,
      heatmap_group_scores,
      heatmap_pillar_insight_ids,
      heatmap_taxonomy,
      heatmap_available_segments,
      heatmap_current_segment,
      heatmap_selected_account_id,
      heatmap_best_performer_account_id,
    },
  } = usePage<
    BizminerInertiaSharedProps & {
      heatmap_accounts?: BizminerInertiaSharedProps["sidebar"]["accounts"];
      heatmap_scores?: HeatmapScoresProp;
      heatmap_benchmarks?: HeatmapBenchmarksProp;
      heatmap_kpi_mapping?: HeatmapKpiMappingProp;
      heatmap_kpi_driver_scores?: HeatmapKpiDriverScoresProp;
      heatmap_group_scores?: HeatmapGroupScoresProp;
      heatmap_pillar_insight_ids?: HeatmapPillarInsightIdsProp;
      heatmap_taxonomy?: BizminerHeatmapTaxonomy;
      heatmap_available_segments?: BizminerHeatmapSegment[];
      heatmap_current_segment?: string;
      heatmap_selected_account_id?: number;
      heatmap_best_performer_account_id?: number;
    }
  >();

  const [benchmarkMode, setBenchmarkMode] =
    React.useState<BenchmarkMode>("avg");

  const {
    taxonomy,
    gapThreshold,
    companyLabel,
    peersLabel,
    accounts,
    accountId,
    selectedAccountName,
    selectedTileId,
    visibleGroups,
    getScore,
    getGroupScore,
    bestPerformerId,
    benchmarkLoading,
    selectedTotal,
    maxTotalScore,
    activeTile,
    activeTileScore,
    activeStatus,
    activeGap,
    kpiQuestions,
    points,
    kpiDriverUrls,
    onSelectTile,
    onCloseDrawer,
  } = useHeatmapState({
    heatmap_accounts,
    heatmap_scores,
    heatmap_benchmarks,
    heatmap_kpi_mapping,
    heatmap_kpi_driver_scores,
    heatmap_group_scores,
    heatmap_pillar_insight_ids,
    heatmap_taxonomy,
    heatmap_selected_account_id,
    heatmap_best_performer_account_id,
    benchmarkMode,
  });

  // ── Side-effect handlers (Inertia navigation + binary export) ──

  const reportsPath = React.useMemo(
    () =>
      bizminer_app_reports_path(undefined, {
        account_id: undefined,
      }),
    [],
  );

  const navigateHeatmapSelection = React.useCallback(
    (next: { segment?: string | null; accountId?: string | number | null }) => {
      const params: { segment?: string; account_id?: string | number } = {};
      if (next.segment) params.segment = next.segment;
      if (next.accountId != null && next.accountId !== "") {
        params.account_id = next.accountId;
      }

      router.get(reportsPath, params, {
        preserveState: true,
        preserveScroll: true,
      });
    },
    [reportsPath],
  );

  const handleSegmentChange = React.useCallback(
    (slug: string) => {
      const segment = heatmap_available_segments?.find((entry) => entry.slug === slug);
      navigateHeatmapSelection({
        segment: slug,
        accountId: segment?.default_account_id ?? null,
      });
    },
    [heatmap_available_segments, navigateHeatmapSelection],
  );

  const handleAccountChange = React.useCallback(
    (value: string) => {
      navigateHeatmapSelection({
        segment: heatmap_current_segment,
        accountId: value,
      });
    },
    [heatmap_current_segment, navigateHeatmapSelection],
  );

  const handleBestPerformer = React.useCallback(() => {
    if (bestPerformerId == null) return;
    navigateHeatmapSelection({
      segment: heatmap_current_segment,
      accountId: bestPerformerId,
    });
  }, [bestPerformerId, heatmap_current_segment, navigateHeatmapSelection]);

  const [exporting, setExporting] = React.useState(false);

  const onExportPptx = React.useCallback(async () => {
    if (!accountId) return;
    setExporting(true);

    try {
      const response = await fetch(
        bizminer_app_reports_export_heatmap_pptx_path(undefined),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || "",
          },
          body: JSON.stringify({
            account_id: accountId,
            segment: heatmap_current_segment,
            benchmark_mode: benchmarkMode,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("[Heatmap Export]", errorData?.error);
        toast.error(
          "Download failed. Please try again or contact your administrator.",
        );
        return;
      }

      const blob = await response.blob();
      const filename =
        response.headers
          .get("Content-Disposition")
          ?.match(/filename="(.+?)"/)?.[1] || "heatmap.pptx";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[Heatmap Export]", error);
      toast.error(
        "Download failed. Please try again or contact your administrator.",
      );
    } finally {
      setExporting(false);
    }
  }, [accountId, heatmap_current_segment, benchmarkMode]);

  // ── Render ────────────────────────────────────────────────

  if (!accounts.length) {
    return (
      <BizminerLayout>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">
            {taxonomy?.meta?.title ?? "Heatmap"}
          </h1>
          <p className="text-muted-foreground">
            No accounts with unlocked AI Maturity and a supported segment
            mapping were found. Unlock AI Maturity for at least one account to
            view the heatmap.
          </p>
        </div>
      </BizminerLayout>
    );
  }

  if (accountId == null) return null;

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <HeatmapHeader
          title={taxonomy?.meta?.title ?? "Heatmap"}
          subtitle={`${selectedAccountName} ${BENCHMARK_SUBTITLES[benchmarkMode]}`}
          tileScaleLabel="Tile scale: 0–5 (5 = best)"
          total={selectedTotal}
          maxTotal={maxTotalScore}
          selectedAccountValue={String(accountId)}
          accounts={accounts}
          onSelectAccount={handleAccountChange}
          bestPerformerDisabled={
            bestPerformerId == null || bestPerformerId === accountId
          }
          onSelectBestPerformer={handleBestPerformer}
          benchmarkMode={benchmarkMode}
          onBenchmarkChange={setBenchmarkMode}
          benchmarkLoading={benchmarkLoading}
          exportDisabled={!accountId || exporting}
          exporting={exporting}
          onExportPptx={onExportPptx}
          segments={heatmap_available_segments}
          currentSegment={heatmap_current_segment}
          onSelectSegment={handleSegmentChange}
        />

        <div className="space-y-8">
          {benchmarkLoading ? (
            <div
              role="status"
              className="rounded-lg border border-border/60 bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              Segment benchmarks are loading. Comparison values will appear
              automatically.
            </div>
          ) : null}

          {visibleGroups.map((group: BizminerHeatmapTaxonomy["groups"][number], groupIndex: number) => {
            const { avg, status: groupStatus } = getGroupScore(group);

            return (
              <section
                key={group.id}
                className={cn(
                  groupIndex > 0 && "border-t border-border/60 pt-8",
                )}
              >
                <div className="grid gap-4 grid-cols-1 xl:grid-cols-[auto_1fr] items-start">
                  <div className="xl:row-span-2">
                    <HeatmapGroupCard
                      name={group.label.toUpperCase()}
                      companyScore={avg.company}
                      peersScore={avg.peers}
                      status={groupStatus}
                      rowSpan={2}
                      companyLabel={companyLabel}
                      peersLabel={peersLabel}
                    />
                  </div>

                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr">
                    {group.tiles.map((tile: BizminerHeatmapTaxonomy["groups"][number]["tiles"][number]) => {
                      const score = getScore(accountId, tile.id);
                      const key = statusKey(
                        score.company,
                        score.peers,
                        gapThreshold,
                      );

                      const selected = selectedTileId === tile.id;
                      const dimmed = Boolean(selectedTileId) && !selected;

                      return (
                        <HeatmapTileButton
                          key={tile.id}
                          tile={tile}
                          score={score}
                          status={key}
                          dimmed={dimmed}
                          selected={selected}
                          onSelect={onSelectTile}
                          companyLabel={companyLabel}
                          peersLabel={peersLabel}
                        />
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <HeatmapDrawer
          open={selectedTileId !== null}
          onClose={onCloseDrawer}
          activeTile={activeTile ?? null}
          activeTileScore={activeTileScore}
          activeStatus={activeStatus}
          activeGap={activeGap}
          kpiQuestions={kpiQuestions}
          points={points}
          kpiDriverUrls={kpiDriverUrls}
          onNavigate={navigateToUrl}
        />
      </div>
    </BizminerLayout>
  );
}
